/**
 * CRT overlay controller — toggles the pure-CSS `#crt-overlay` layer
 * (crt.css). Two independent switches gate it:
 *
 *  - `setCrtEnabled(bool)` — the persisted USER preference. Defaults to ON,
 *    except when `prefers-reduced-motion` matches (then OFF, per plan §6).
 *    The OPTIONS screen wires a control to this in M3.
 *  - `setCrtVisible(bool)` — the runtime switch: main.ts turns it off during
 *    live gameplay (no scanlines over the arena) and back on in the menus.
 *
 * The overlay renders only when enabled AND visible.
 */

export interface CrtStorage {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
}

const CRT_KEY = "neon-arena.crt-enabled";

let enabled = true;
let visible = true;
let storageRef: CrtStorage | null = null;

function defaultStorage(): CrtStorage | null {
  try {
    return typeof localStorage !== "undefined" ? localStorage : null;
  } catch {
    return null;
  }
}

function apply(): void {
  if (typeof document === "undefined") return;
  const el = document.getElementById("crt-overlay");
  if (el) el.classList.toggle("crt-off", !(enabled && visible));
}

/** Read the persisted preference (reduced-motion default) and apply it. */
export function initCrtOverlay(storage: CrtStorage | null = defaultStorage()): void {
  storageRef = storage;
  let stored: string | null = null;
  try {
    stored = storage?.getItem(CRT_KEY) ?? null;
  } catch {
    stored = null;
  }
  if (stored !== null) {
    enabled = stored === "true";
  } else {
    const reduced =
      typeof window !== "undefined" &&
      typeof window.matchMedia === "function" &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    enabled = !reduced;
  }
  apply();
}

export function setCrtEnabled(on: boolean): void {
  enabled = on;
  try {
    storageRef?.setItem(CRT_KEY, String(on));
  } catch {
    /* non-fatal */
  }
  apply();
}

export function getCrtEnabled(): boolean {
  return enabled;
}

export function setCrtVisible(v: boolean): void {
  visible = v;
  apply();
}
